import type { NativeInvoiceLineAccounting } from "@common/schemas/domains/invoice/native-invoice.schema";
import { buildLedgerOverride } from "@lib/accountOverrides";
import type { NativeInvoiceLineCodingByLineId } from "./nativeInvoiceFormTypes";

interface NativeInvoiceLineCodingFields {
    id: string;
    classIds: string[];
    accrualAccountId: string | null;
    receivablesAccountId: string | null;
}

function sameClassIds(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
        return false;
    }
    const sortedLeft = [...left].toSorted((a, b) => a.localeCompare(b));
    const sortedRight = [...right].toSorted((a, b) => a.localeCompare(b));
    return sortedLeft.every((id, index) => id === sortedRight[index]);
}

function codingFromBaseline(
    baseline: NativeInvoiceLineCodingByLineId | undefined,
    lineId: string,
): {
    classIds: string[];
    accrualAccountId: string | null;
    receivablesAccountId: string | null;
} {
    const entry = baseline?.[lineId];
    return {
        classIds: entry?.classIds ?? [],
        accrualAccountId: entry?.accrualAccountId ?? null,
        receivablesAccountId: entry?.receivablesAccountId ?? null,
    };
}

/**
 * Builds the `lineAccounting` save payload as a diff against the last-known
 * (hydrated or previously saved) coding. Emits clear-ops when a dimension goes
 * from a value to empty/`null` (`classIds: []`, `accountId: null`) so the
 * server — which treats an omitted dimension as "no change" — actually wipes
 * the persisted override/class.
 */
export function buildNativeInvoiceLineAccounting({
    lines,
    primaryAccrualLedgerId,
    incomeAccountLedgerId,
    baselineByLineId,
}: {
    lines: readonly NativeInvoiceLineCodingFields[];
    primaryAccrualLedgerId: string | undefined;
    // Income account posts to the accrual ledger when the entity has one, and
    // to the cash ledger otherwise. Callers that only know the accrual ledger
    // keep the previous target.
    incomeAccountLedgerId?: string;
    baselineByLineId?: NativeInvoiceLineCodingByLineId;
}): NativeInvoiceLineAccounting {
    const incomeLedgerId = incomeAccountLedgerId ?? primaryAccrualLedgerId;
    return lines.flatMap((line) => {
        const baseline = codingFromBaseline(baselineByLineId, line.id);
        const classesChanged = !sameClassIds(line.classIds, baseline.classIds);
        const accrualChanged =
            line.accrualAccountId !== baseline.accrualAccountId;
        const receivablesChanged =
            line.receivablesAccountId !== baseline.receivablesAccountId;

        if (!classesChanged && !accrualChanged && !receivablesChanged) {
            return [];
        }

        const accountOverrides = accrualChanged
            ? buildLedgerOverride(incomeLedgerId, line.accrualAccountId)
            : [];
        const accountsReceivableOverrides = receivablesChanged
            ? buildLedgerOverride(
                  primaryAccrualLedgerId,
                  line.receivablesAccountId,
              )
            : [];

        if (
            !classesChanged &&
            accountOverrides.length === 0 &&
            accountsReceivableOverrides.length === 0
        ) {
            // Account dimension changed but we have no ledger to target —
            // nothing safe to send (can't clear/set without a ledger id).
            return [];
        }

        return [
            {
                lineId: line.id,
                ...(classesChanged ? { classIds: line.classIds } : {}),
                ...(accountOverrides.length > 0 ? { accountOverrides } : {}),
                ...(accountsReceivableOverrides.length > 0
                    ? { accountsReceivableOverrides }
                    : {}),
            },
        ];
    });
}

export function lineCodingBaselineFromLines(
    lines: readonly NativeInvoiceLineCodingFields[],
): NativeInvoiceLineCodingByLineId {
    return Object.fromEntries(
        lines.map((line) => [
            line.id,
            {
                classIds: line.classIds,
                accrualAccountId: line.accrualAccountId,
                receivablesAccountId: line.receivablesAccountId,
            },
        ]),
    );
}
